'use client';

import { isAxiosError } from 'axios';
import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { flatMap, keyBy } from 'es-toolkit';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { PropsWithChildren, createContext } from 'react';

import {
  getExchangeRatesApi,
  getLuckyDrawApi,
  getLuckyDrawMissionsApi,
  postRegisterLuckyDrawApi,
} from '@flipster/apis/v1/campaign';
import { GetLuckyDrawResponse } from '@flipster/apis/v1/campaign/types';
import { i18nBunja } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { ServerError } from '@flipster/utils/constants/server-error';

import { abTestBunja } from '../../shared/ab-test';
import { authBunja } from '../../shared/auth/state';
import { LuckyDrawItemFragment, MissionItemFragment } from './query.generated';

const SlugContext = createContext<string | null>(null);
const SlugScope = createScopeFromContext(SlugContext);

export type LuckyDrawsLayoutType = LuckyDrawItemFragment[] | null | undefined;
const LuckyDrawsLayoutContext = createContext<LuckyDrawsLayoutType>(undefined);
const LuckyDrawsLayoutScope = createScopeFromContext(LuckyDrawsLayoutContext);

export type MissionsLayoutType = MissionItemFragment[] | null | undefined;
const MissionsLayoutContext = createContext<MissionsLayoutType>(undefined);
const MissionsLayoutScope = createScopeFromContext(MissionsLayoutContext);

export const LuckyDrawProvider = ({
  slug,
  luckyDraws,
  missions,
  children,
}: PropsWithChildren<{
  slug: string;
  luckyDraws: LuckyDrawsLayoutType;
  missions: MissionsLayoutType;
}>) => {
  return (
    <SlugContext value={slug}>
      <LuckyDrawsLayoutContext value={luckyDraws}>
        <MissionsLayoutContext value={missions}>{children}</MissionsLayoutContext>
      </LuckyDrawsLayoutContext>
    </SlugContext>
  );
};

export const luckyDrawBunja = bunja(() => {
  const slug = bunja.use(SlugScope);
  const luckyDrawsLayout = bunja.use(LuckyDrawsLayoutScope);
  const { prexInstallationId } = bunja.use(abTestBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const store = bunja.use(JotaiStoreScope);
  const { getT } = bunja.use(i18nBunja);

  const t = getT();
  const luckyDrawSlugs = luckyDrawsLayout?.map((l) => l.slug).filter(Boolean);

  const luckyDrawWithQueries = atom((get) => {
    const isLoggedIn = get(isCookieLoggedInAtom);
    if (!luckyDrawSlugs) return [];
    return luckyDrawSlugs.map((slug) => {
      const queryKey = ['getLuckyDrawApi', slug, isLoggedIn, prexInstallationId];
      return atomWithQuery(() => ({
        queryKey,
        queryFn: () => getLuckyDrawApi({ eventId: slug, installId: prexInstallationId }),
        select: (data) => ({ id: slug, queryKey, ...data.data }),
        refetchOnMount: true,
      }));
    });
  });

  const luckyDrawsAtom = atom((get) => {
    const queryAtoms = get(luckyDrawWithQueries);
    const luckyDrawQueries = queryAtoms.map((queryAtom) => get(queryAtom));
    if (luckyDrawQueries.some((query) => query.status !== 'success')) return [];
    return luckyDrawQueries.map((queryAtom) => queryAtom.data);
  });

  const isRegisteredAtom = atom((get) => {
    const luckyDraws = get(luckyDrawsAtom);
    return luckyDraws.every((luckyDraw) => luckyDraw?.isRegistered);
  });

  const rewardsAtom = atom((get) => {
    const luckyDraws = get(luckyDrawsAtom);
    // 반대로 뒤집어야 높은 상품부터 보여서 한번 뒤집어서 작업
    return flatMap([...luckyDraws].reverse(), (luckyDraw) => luckyDraw?.prizes ?? []);
  });

  const invalidLuckyDraws = () => {
    store.get(queryClientAtom).refetchQueries({ queryKey: ['getLuckyDrawApi'], type: 'active' });
  };

  const setNewDrawResultAtom = atom(null, (get, set, values: { slug: string; token?: number; prizeId?: string }) => {
    const queryClient = get(queryClientAtom);
    const luckyDraws = get(luckyDrawsAtom);
    const luckyDraw = luckyDraws.find((l) => l?.id === values.slug);

    if (!luckyDraw) {
      invalidLuckyDraws();
      return;
    }

    queryClient.setQueryData<{ data: { id: string } & GetLuckyDrawResponse }>(luckyDraw.queryKey, (prevState) => {
      if (!prevState) {
        invalidLuckyDraws();
        return prevState;
      }

      let prizes = prevState.data.prizes;
      if (values.prizeId) {
        prizes = [...prevState.data.prizes];
        const index = prizes.findIndex((p) => p.id === values.prizeId);
        if (index >= 0) {
          prizes[index] = {
            ...prizes[index],
            count: prizes[index].count + 1,
          };
        } else {
          prizes.push({ id: values.prizeId, count: 1 });
        }
      }
      return {
        ...prevState,
        data: {
          ...prevState.data,
          token: values.token ?? prevState.data.token,
          prizes,
        },
      };
    });
  });

  const registerMutationAtom = atomWithMutation(() => ({
    mutationFn: () => postRegisterLuckyDrawApi({ slugIds: luckyDrawSlugs ?? [] }),
    onSuccess() {
      snackbar.success({ message: t('Campaign.r251124.You_are_registered_event') });
      invalidLuckyDraws();
    },
    onError(error) {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  }));

  const exchangeCostQueryAtom = atomWithQuery(() => ({
    queryKey: ['getExchangeRatesApi', slug],
    queryFn: () => getExchangeRatesApi({ eventId: slug! }),
    select: (data) => keyBy(data.data.pairs, (pair) => pair.to),
    enabled: !!slug,
  }));

  return {
    slug,
    luckyDrawSlugs,
    luckyDrawsLayout,
    luckyDrawsAtom,
    isRegisteredAtom,
    rewardsAtom,
    registerMutationAtom,
    exchangeCostQueryAtom,
    invalidLuckyDraws,
    setNewDrawResultAtom,
  };
});
luckyDrawBunja.debugLabel = 'luckyDrawBunja';

export const missionsBunja = bunja(() => {
  const { luckyDrawSlugs } = bunja.use(luckyDrawBunja);
  const missionsLayout = bunja.use(MissionsLayoutScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const groups = missionsLayout?.map((l) => l?.groupName ?? 'unknown');

  const missionsWithQueries = atom((get) => {
    const isLoggedIn = get(isCookieLoggedInAtom);
    if (!luckyDrawSlugs) return [];
    return luckyDrawSlugs.map((slug) =>
      atomWithQuery(() => ({
        queryKey: ['getLuckyDrawMissionsApi', slug, isLoggedIn],
        queryFn: () => getLuckyDrawMissionsApi({ eventId: slug }),
        select: (data) => data.data,
        refetchOnMount: true,
      })),
    );
  });

  const missionsAtom = atom((get) => {
    const queryAtoms = get(missionsWithQueries);
    const queries = queryAtoms.map((queryAtom) => get(queryAtom));
    if (queries.some((q) => q.status !== 'success')) return null;
    return keyBy(
      flatMap(queries, (q) => q?.data?.missions ?? []),
      (m) => m.id,
    );
  });

  return { missionsLayout, missionsAtom, groups };
});
missionsBunja.debugLabel = 'missionsBunja';
